<template>
  <b-row v-if="portfolioType !== 'u'">
    <b-col>
      <b-card class="mb-1">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <div class="d-flex align-items-center">
            <b-card-title class="mb-0">
              Product Upcoming Events
            </b-card-title>
            <hint-popup
              :id="38"
              class="ml-50"
            />
          </div>
          <b-button
            v-if="!groupEventsTable"
            variant="outline-secondary"
            size="sm"
            class="d-flex align-items-center"
            @click="downloadCsv"
          >
            <b-img
              :src="require('@/assets/images/icons/file-icons/csv.png')"
              height="20"
              width="20"
            />
            <span class="ml-50 font-weight-bold text-body">Download CSV</span>
          </b-button>
        </div>
        <div
          v-if="!eventsFailed"
          class="d-flex align-items-center mb-2"
        >
          <b-form-group class="mb-0">
            <b-form-radio-group
              v-model="groupEventsTable"
              :options="groupOptions"
              name="events-group-selection"
              button-variant="outline-primary"
              buttons
            />
          </b-form-group>
        </div>

        <div
          v-if="eventsFailed"
          class="d-flex flex-column align-items-center justify-content-center py-5"
        >
          <feather-icon
            icon="AlertTriangleIcon"
            size="64"
            class="text-warning mb-2"
          />
          <p>{{ eventsFailedMessage }}</p>
        </div>

        <div v-else>
          <div class="font-weight-bold mb-1">
            {{ eventsCounts ? eventsCounts.shown : pagination.totalRows }} results found
            <span
              v-if="eventsCounts && eventsCounts.restricted > 0"
              class="text-muted font-weight-normal"
            >(further {{ eventsCounts.restricted }} results outside your date permission window. The latest date viewable is {{ formatIsoDate(dateBoundary) }})</span>
            <br v-if="pagination.totalRows > pagination.perPage">
            <span
              v-if="pagination.totalRows > pagination.perPage"
              class="font-weight-normal"
            >Showing {{ showing[0] }} to {{ showing[1] }} of {{ pagination.totalRows }}</span>
          </div>
          <b-card
            id="all-upcoming-table"
            no-body
          >
            <b-table
              v-if="!pagination.error"
              responsive
              hover
              show-empty
              :items="itemsWithEvents"
              :fields="computedFields"
              :busy="pagination.loading"
              :sort-by.sync="pagination.sortBy"
              :sort-desc.sync="pagination.sortDesc"
              :sort-direction="pagination.sortDirection"
              :no-local-sorting="true"
              table-class="events-table-base"
              tbody-tr-class="events-table-row"
              @row-clicked="productClicked"
            >
              <template #table-busy>
                <div class="text-center py-5">
                  <b-spinner variant="primary" />
                </div>
              </template>
              <template #cell(toggle)="data">
                <b-button
                  variant="outline-primary"
                  class="btn-icon rounded-circle"
                  @click.stop="data.toggleDetails"
                >
                  <feather-icon :icon="data.detailsShowing ? 'ChevronUpIcon' : 'ChevronDownIcon'" />
                </b-button>
              </template>

              <template #cell(producttype)="data">
                <div class="d-flex align-items-center">
                  <b-avatar
                    class="mr-1"
                    :variant="data.item.pticoncolor || 'light-secondary'"
                  >
                    <feather-icon
                      size="18"
                      :icon="data.item.pticonname || 'ArrowRightIcon'"
                    />
                  </b-avatar>
                  <span>{{ data.item.producttypename }}</span>
                </div>
              </template>

              <template #head(status)="data">
                <span class="d-inline-flex align-items-center">
                  {{ data.label }}
                  <hint-popup
                    :id="39"
                    class="ml-50"
                  />
                </span>
              </template>

              <template #head(probability)="data">
                <span class="d-inline-flex align-items-center">
                  {{ data.label }}
                  <hint-popup
                    :id="40"
                    class="ml-50"
                  />
                </span>
              </template>

              <template #cell(productid)="data">
                <span class="font-weight-bold">{{ data.item.productid }}</span>
              </template>

              <template #cell(notional)="data">
                {{ formatNotional(data.item.notional) }}
              </template>

              <template #cell(eventdate)="data">
                <span v-if="!data.detailsShowing || !groupEventsTable">{{ formatIsoDate(data.item.eventdate) }}</span>
              </template>

              <template #cell(eventname)="data">
                <div
                  v-if="!data.detailsShowing || !groupEventsTable"
                  class="events-table-grid-container"
                >
                  <div
                    v-for="(eventName, index) in data.item.eventname"
                    :key="index"
                    :class="`events-table-grid-item ${(index + 1 < data.item.eventname.length) ? 'events-table-grid-divider' : ''}`"
                  >
                    <span>{{ eventName }}</span>
                  </div>
                </div>
              </template>

              <template #cell(status)="data">
                <div
                  v-if="!data.detailsShowing || !groupEventsTable"
                  class="events-table-grid-container"
                >
                  <div
                    v-for="(status, index) in data.item.status"
                    :key="index"
                    :class="`events-table-grid-item ${(index + 1 < data.item.eventname.length) ? 'events-table-grid-divider' : ''}`"
                  >
                    <feather-icon
                      v-if="status"
                      :icon="status"
                      :class="`text-${data.item.color[index]} mr-1`"
                      size="18"
                    />
                    <span v-if="data.item.level[index]">
                      {{ data.item.level[index] }}%
                    </span>
                  </div>
                </div>
              </template>

              <template #cell(probability)="data">
                <div
                  v-if="!data.detailsShowing || !groupEventsTable"
                  class="events-table-grid-container"
                >
                  <div
                    v-for="(probability, index) in data.item.probability"
                    :key="index"
                    :class="`events-table-grid-item ${(index + 1 < data.item.eventname.length) ? 'events-table-grid-divider' : ''}`"
                  >
                    <span v-if="probability && parseFloat(probability) !== 0">
                      {{ Math.round(parseFloat(probability * 100)) }}%
                    </span>
                  </div>
                </div>
              </template>

              <template #row-details="data">
                <b-card
                  class="p-0 ml-3 expanded-events-card"
                  no-body
                >
                  <div
                    class="d-flex justify-content-between align-items-center px-2 expanded-events-card-header"
                  >
                    <span class="font-weight-bold">
                      All product events
                    </span>
                  </div>
                  <b-table
                    :items="data.item.events"
                    :fields="eventFields"
                    class="expanded-events-table"
                    borderless
                    responsive
                    small
                  >
                    <template #head(status)="header">
                      <span class="d-inline-flex align-items-center">
                        {{ header.label }}
                        <hint-popup
                          :id="39"
                          class="ml-50"
                        />
                      </span>
                    </template>
                    <template #head(probability)="header">
                      <span class="d-inline-flex align-items-center">
                        {{ header.label }}
                        <hint-popup
                          :id="40"
                          class="ml-50"
                        />
                      </span>
                    </template>
                    <template #cell(eventdate)="eventData">
                      {{ formatIsoDate(eventData.item.eventdate) }}
                    </template>
                    <template #cell(eventname)="eventData">
                      <div class="events-table-grid-container">
                        <div
                          v-for="(eventName, index) in eventData.item.eventname"
                          :key="index"
                          :class="`events-table-grid-item ${(index + 1 < eventData.item.eventname.length) ? 'events-table-grid-divider' : ''}`"
                        >
                          <span>{{ eventName }}</span>
                        </div>
                      </div>
                    </template>
                    <template #cell(status)="eventData">
                      <div class="events-table-grid-container">
                        <div
                          v-for="(status, index) in eventData.item.status"
                          :key="index"
                          :class="`events-table-grid-item ${(index + 1 < eventData.item.eventname.length) ? 'events-table-grid-divider' : ''}`"
                        >
                          <feather-icon
                            v-if="status"
                            :icon="status"
                            :class="`text-${eventData.item.color[index]} mr-1`"
                            size="18"
                          />
                          <span v-if="eventData.item.level[index]">
                            {{ eventData.item.level[index] }}%
                          </span>
                        </div>
                      </div>
                    </template>
                    <template #cell(probability)="eventData">
                      <div class="events-table-grid-container">
                        <div
                          v-for="(probability, index) in eventData.item.probability"
                          :key="index"
                          :class="`events-table-grid-item ${(index + 1 < eventData.item.eventname.length) ? 'events-table-grid-divider' : ''}`"
                        >
                          <span v-if="probability && parseFloat(probability) !== 0">
                            {{ Math.round(parseFloat(probability * 100)) }}%
                          </span>
                        </div>
                      </div>
                    </template>
                  </b-table>
                </b-card>
              </template>
            </b-table>

            <div
              v-if="itemsWithEvents.length > 0"
              class="d-flex justify-content-between px-2"
            >
              <div class="my-2">
                <span class="pb-1">Rows Per Page</span>
                <b-form-select
                  v-model="pagination.perPage"
                  :options="pagination.perPageOptions"
                  class="pagination-select"
                />
              </div>

              <div class="my-2 d-flex flex-column justify-content-end">
                <b-pagination
                  v-model="pagination.currentPage"
                  :total-rows="pagination.totalRows"
                  :per-page="pagination.perPage"
                  align="center"
                  size="lg"
                  class="mb-0"
                />
              </div>
            </div>
          </b-card>
        </div>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardTitle,
  BFormGroup,
  BFormRadioGroup,
  BTable,
  BAvatar,
  BSpinner,
  BFormSelect,
  BPagination,
  BButton,
  BImg,
} from 'bootstrap-vue'
import { formatIsoDate } from '@/utils/formatters'
import HintPopup from '@/views/components/HintPopup.vue'

export default {
  components: {
    HintPopup,
    BRow,
    BCol,
    BCard,
    BCardTitle,
    BFormGroup,
    BFormRadioGroup,
    BTable,
    BAvatar,
    BSpinner,
    BFormSelect,
    BPagination,
    BButton,
    BImg,
  },
  props: {
    productWeightMode: {
      type: String,
      default: 'equal',
    },
    portfolioType: {
      type: [String, Number],
      default: 'u',
    },
    searchFilter: {
      type: Object,
      default: null,
    },
    portfolioCounts: {
      type: Object,
      default: null,
    },
    portfolioName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      eventsFailed: false,
      eventsRequestId: 0,
      eventsFailedMessage: undefined,
      groupEventsTable: false,
      groupOptions: [
        { text: 'Single', value: false },
        { text: 'Grouped', value: true },
      ],
      upcomingData: {
        items: [],
      },
      dateBoundary: null,
      pagination: {
        currentPage: 1,
        perPage: 10,
        previousPage: null,
        nextPage: null,
        totalPages: 0,
        totalRows: 0,
        perPageOptions: [5, 10, 20, 50, 100],
        loading: true,
        error: false,
        loadingContainerHeight: '200px',
        filter: '',
        sortBy: 'eventdate',
        sortDesc: false,
        sortDirection: 'asc',
      },
      tableFields: [
        {
          key: 'productid', label: 'Id', thStyle: { width: '5%' }, sortable: true,
        },
        {
          key: 'producttype', label: 'Type', thStyle: { width: '10%' }, sortable: true,
        },
        {
          key: 'productname', label: 'Product', thStyle: { width: '33%' }, sortable: true,
        },
        {
          key: 'eventdate', label: 'Date', thStyle: { width: '12%' }, sortable: true,
        },
        {
          key: 'eventname', label: 'Event', thStyle: { width: '22%' }, sortable: true, tdClass: 'p-0',
        },
        {
          key: 'status', label: 'Level', thStyle: { width: '12%' }, sortable: true, tdClass: 'p-0',
        },
        {
          key: 'probability', label: 'Probability', thStyle: { width: '8%' }, sortable: true, tdClass: 'p-0',
        },
      ],
      eventFields: [
        {
          key: 'eventnum', label: 'Position', thStyle: { width: '10%' }, sortable: true,
        },
        {
          key: 'eventdate', label: 'Date', thStyle: { width: '22%' }, sortable: true,
        },
        {
          key: 'eventname', label: 'Event', thStyle: { width: '40%' }, tdClass: 'p-0',
        },
        {
          key: 'status', label: 'Level', thStyle: { width: '15%' }, tdClass: 'p-0',
        },
        {
          key: 'probability', label: 'Probability', thStyle: { width: '13%' }, tdClass: 'p-0',
        },
      ],
    }
  },
  computed: {
    eventsCounts() {
      if (!this.portfolioCounts) return null
      return this.portfolioCounts.UPCOMINGEVENTS
    },
    showing() {
      const from = (this.pagination.currentPage - 1) * this.pagination.perPage + 1
      const to = Math.min(this.pagination.currentPage * this.pagination.perPage, this.pagination.totalRows)
      return [from, to]
    },
    itemsWithEvents() {
      const { items } = this.upcomingData
      const filteredItems = []

      items.forEach(i => {
        if (this.groupEventsTable) {
          // Grouped mode: show one row per product with first event data
          // Keep the events array for row-details expansion
          let eventWithDetail = { ...i }
          if (i.events && i.events.length > 0) {
            eventWithDetail = {
              ...eventWithDetail,
              ...i.events[0],
            }
          }
          filteredItems.push(eventWithDetail)
        } else if (i.events) {
          // Ungrouped mode: push each event as a separate row
          i.events.forEach(e => {
            const eventWithDetail = { ...i, ...e }
            filteredItems.push(eventWithDetail)
          })
        } else {
          filteredItems.push(i)
        }
      })

      return filteredItems
    },
    computedFields() {
      const fields = this.tableFields.slice()
      if (this.productWeightMode === 'notional') {
        const productIndex = fields.findIndex(field => field.key === 'productname')
        fields[productIndex] = { ...fields[productIndex], thStyle: { width: '23%' } }
        fields.splice(productIndex + 1, 0, {
          key: 'notional', label: 'Notional', thStyle: { width: '10%' },
        })
      }
      if (this.groupEventsTable) {
        fields.unshift({
          key: 'toggle',
          label: 'Show All Events',
          thStyle: { width: '5%' },
        })
      }
      return fields
    },
  },
  watch: {
    productWeightMode() {
      this.upcomingData.items = []
      if (this.pagination.currentPage === 1) {
        this.loadUpcomingEvents()
      } else {
        this.pagination.currentPage = 1
      }
    },
    'pagination.currentPage': function onCurrentPageChange() {
      this.loadUpcomingEvents()
    },
    'pagination.perPage': function onPerPageChange() {
      if (this.pagination.currentPage === 1) {
        this.loadUpcomingEvents()
      } else {
        this.pagination.currentPage = 1
      }
    },
    'pagination.sortBy': function onSortByChange() {
      this.loadUpcomingEvents()
    },
    'pagination.sortDesc': function onSortDescChange() {
      this.loadUpcomingEvents()
    },
    groupEventsTable() {
      this.loadUpcomingEvents()
    },
  },
  mounted() {
    if (this.portfolioType !== 'u') {
      this.loadUpcomingEvents()
    }
  },
  methods: {
    formatNotional(value) {
      if (value === null || value === undefined || value === '') return '-'
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed.toLocaleString() : String(value)
    },
    loadUpcomingEvents() {
      this.eventsRequestId += 1
      const requestId = this.eventsRequestId
      if (this.portfolioType === 'u') {
        this.pagination.loading = false
        return
      }

      this.pagination.loading = true
      this.pagination.error = null

      // Determine which API endpoint to use based on portfolio type
      let upcomingEndpoint
      const upcomingParams = {
        page: this.pagination.currentPage,
        perpage: this.pagination.perPage,
        search: this.pagination.filter,
        sortBy: this.pagination.sortBy,
        sortDesc: this.pagination.sortDesc,
        grouped: this.groupEventsTable,
      }

      if (this.portfolioType === 'v') {
        upcomingEndpoint = 'lifecycle/public/report.cfc?method=getVirtualPortfolioAllUpcoming'
        upcomingParams.searchfilter = JSON.stringify(this.searchFilter)
      } else {
        upcomingEndpoint = 'lifecycle/public/report.cfc?method=getUserPortfolioAllUpcoming'
        upcomingParams.portfolioid = this.portfolioType
        upcomingParams.productweightingmode = this.productWeightMode
      }

      this.$http
        .get(upcomingEndpoint, { params: upcomingParams })
        .then(response => {
          if (requestId !== this.eventsRequestId) return
          if (!response.data.STATUS) {
            this.eventsFailed = true
            this.eventsFailedMessage = response.data.message
          } else {
            this.eventsFailed = false
            this.eventsFailedMessage = undefined
            // Fill the data
            this.upcomingData.items = response.data.DATA
            // eslint-disable-next-line indent
              // Update pagination data
            this.pagination.previousPage = response.data.INFO.PREVIOUSPAGE
            this.pagination.nextPage = response.data.INFO.NEXTPAGE
            this.pagination.currentPage = response.data.INFO.CURRENTPAGE * 1
            this.pagination.totalPages = response.data.INFO.NUMPAGES
            this.pagination.perPage = response.data.INFO.PERPAGE
            this.pagination.totalRows = response.data.INFO.NUMROWS
            this.dateBoundary = response.data.INFO.DATEBOUNDARY || null
          }
        })
        .catch(() => {
          if (requestId !== this.eventsRequestId) return
          this.pagination.error = true
        })
        .finally(() => {
          if (requestId !== this.eventsRequestId) return
          this.pagination.loading = false
        })
    },
    productClicked(item) {
      if (!item.productid) return
      this.$router.push({ name: 'product-report', params: { reportNumber: item.productid } })
    },
    downloadCsv() {
      this.$emit('download-csv')

      const isVirtual = this.portfolioType === 'v'
      const method = isVirtual ? 'downloadCSVVirtualPortfolioAllUpcoming' : 'downloadCSVUserPortfolioAllUpcoming'
      const params = isVirtual ? {
        searchfilter: JSON.stringify(this.searchFilter),
        sortBy: this.pagination.sortBy,
        sortDesc: this.pagination.sortDesc,
      } : {
        portfolioid: this.portfolioType,
        portfolioname: this.portfolioName,
        productweightingmode: this.productWeightMode,
        sortBy: this.pagination.sortBy,
        sortDesc: this.pagination.sortDesc,
      }
      this.$http.get(`lifecycle/public/report.cfc?method=${method}`, { params, responseType: 'blob' })
        .then(response => {
          const url = window.URL.createObjectURL(response.data)
          const a = document.createElement('a')
          a.style.display = 'none'
          a.href = url
          // eslint-disable-next-line prefer-destructuring
          a.download = response.headers['content-disposition'].match(/"([^']+)"/)[1]
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
        })
    },
    formatIsoDate,
  },
}
</script>

<style lang="scss" scoped>
.btn-icon {
  padding: 0.5rem;
}

.pagination-select {
  width: 90px;
  margin-left: 0.5rem;
}

.events-table-base {
  margin-bottom: 0;
}

.events-table-row {
  cursor: pointer;
}

.events-table-grid-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.events-table-grid-item {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  min-height: 2.5rem;
}

.events-table-grid-divider {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.dark-layout .events-table-grid-divider {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.expanded-events-card {
  background: rgba(26, 115, 232, 0.10);
  margin-bottom: 1rem;
}

.dark-layout .expanded-events-card {
  background: rgba(255, 255, 255, 0.05);
}

.expanded-events-card-header {
  height: 3rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.expanded-events-table {
  max-height: 400px;
  overflow-y: auto;
}

.expanded-events-table::-webkit-scrollbar {
  width: 4px;
  border-radius: 6px;
}

.expanded-events-table::-webkit-scrollbar-track {
  background: rgba(56, 56, 56, 0.49);
  border-radius: 6px;
}

.expanded-events-table::-webkit-scrollbar-thumb {
  background: rgba(26, 115, 232, 0.85);
}
</style>
